import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import {
  SourceName,
  useApplicationTrackErrorEvents,
} from 'features/application/hooks/useApplicationTrackErrorEvents';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { OtpSearchParams } from 'features/otp/constants/OtpSearchParams';
import useResendOtp from 'features/otp/hooks/useResendOtp/useResendOtp';
import useVerifyChallenge from 'features/otp/hooks/useVerifyChallenge/useVerifyChallenge';
import { InformationScreenTypeEnum } from 'features/otp/information/InformationScreenTypeEnum';
import { EMAIL_VERIFICATION_TYPE } from 'features/signup/constants';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import Button from 'shared/components/vxl/Button/Button';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar, {
  SNACKBAR_TIMEOUT,
} from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isWeb } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useWhereToGo from 'shared/hooks/useWhereToGo/useWhereToGo';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import theme from 'shared/styles/theme';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

const VerifyEmail = () => {
  const router = useRouter();
  const [otpSubmitAttempt, setOtpSubmitAttempt] = useState(1);
  const resendOtp = useResendOtp();
  const {
    verifyChallenge,
    isLoading: isVerifyChallengeLoading,
    errorMessage,
    setErrorMessage,
  } = useVerifyChallenge();
  const [resendOtpError, setResendOTPError] = useState<string>('');
  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
  const [isResendLoading, setIsResendLoading] = useState<boolean>(false);
  const [snackBarMessage, setSnackBarMessage] = useState<string>('');
  const [isAutoSubmittingFromDeeplink, setIsAutoSubmittingFromDeeplink] =
    useState<boolean>(false);
  const { segmentClient } = useAnalyticsConfiguration();
  const {
    anonymousId,
    session,
    emailAddress,
    phoneNumber,
    code,
    screenType,
    flowKey,
    loginMethod,
    usernameType,
    deviceNotificationStatus,
  } = useLocalSearchParams<OtpSearchParams>();

  const { fetchWhereToGo, isLoading: isWhereToGoFetching } = useWhereToGo();
  const { isLoggedIn } = useContext<AuthContextProps>(AuthContext);
  const { logErrorEvent } = useApplicationTrackErrorEvents();

  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'verifyEmail',
  });
  const insets = useSafeAreaInsets();

  const handleVerificationCodeSubmitted = usePressableAnalytics({
    analyticsId: 'verificationCodeSubmitted',
    customAttributes: {
      verification_attempt: otpSubmitAttempt,
      verification_type: 'email',
    },
  });

  const signedIn = usePressableAnalytics({
    analyticsId: 'signedIn',
    customAttributes: {
      login_method: loginMethod,
      username_type: usernameType,
      device_notification_status: deviceNotificationStatus,
    },
  });

  useAutoHideSnackbar(
    Boolean(snackBarMessage),
    () => {
      setResendOTPError('');
      setSnackBarMessage('');
    },
    SNACKBAR_TIMEOUT,
  );

  useEffect(() => {
    if (errorMessage) {
      logErrorEvent(errorMessage, SourceName.EMAIL_VERIFICATION);
      setIsAutoSubmittingFromDeeplink(false);
      setSnackBarMessage(errorMessage);
    }
  }, [errorMessage, logErrorEvent]);

  useEffect(() => {
    if (resendOtpError) {
      logErrorEvent(resendOtpError, SourceName.EMAIL_VERIFICATION);
      setSnackBarMessage(resendOtpError);
    }
  }, [logErrorEvent, resendOtpError]);

  useEffect(() => {
    if (isOtpSent) {
      if (snackBarMessage !== translatedText('toast')) {
        setSnackBarMessage(translatedText('toast'));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOtpSent, translatedText]);

  const logout = () => {
    router.dismissAll();
    router.replace('/login');
  };

  useBackHandler(logout);

  const onCodeSubmitAction = useCallback(async () => {
    handleVerificationCodeSubmitted();
    setOtpSubmitAttempt(otpSubmitAttempt + 1);
    await verifyChallenge(
      {
        emailAddress: emailAddress ?? '',
        code: code,
        phoneNumber: '',
        anonymousId: anonymousId ?? (await segmentClient.getAnonymousId()),
        session: session ?? '',
      },
      {
        verifyType: EMAIL_VERIFICATION_TYPE,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, emailAddress, verifyChallenge, session]);

  useEffect(() => {
    if (isLoggedIn) {
      signedIn();
      void fetchWhereToGo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    if (code) {
      setIsAutoSubmittingFromDeeplink(true);
      void onCodeSubmitAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, session]);

  const onEnterCodeSubmitAction = async () => {
    setErrorMessage('');
    router.push({
      pathname:
        flowKey === OtpFlowKey.SIGNUP
          ? '/signup/verify-email-code'
          : '/login/verify-email-code',
      params: {
        anonymousId: anonymousId ?? (await segmentClient.getAnonymousId()),
        emailAddress: emailAddress,
        phoneNumber: phoneNumber,
        session: session,
        screenType: screenType,
        flowKey,
      },
    });
  };

  const onResendLinkAction = async () => {
    setIsResendLoading(true);

    try {
      const result = await resendOtp({
        anonymousId: anonymousId ?? (await segmentClient.getAnonymousId()),
        emailAddress: emailAddress ?? '',
        phoneNumber: phoneNumber ?? '',
      });

      setIsResendLoading(false);

      if (result.message) {
        setResendOTPError(result.message);
        logBreadcrumb({
          level: 'error',
          message: `Signup Resend OTP error: ${result.errorCode}, ${result.message}, ${result.title}`,
        });
      } else {
        setIsOtpSent(true);
      }
    } catch {
      setResendOTPError(translatedText('genericError'));
      router.navigate({
        pathname: '/otp/error',
        params: { screenType: InformationScreenTypeEnum.Error },
      });
    }

    try {
      await resendOtp({
        anonymousId: anonymousId ?? (await segmentClient.getAnonymousId()),
        emailAddress: emailAddress ?? '',
        phoneNumber: phoneNumber ?? '',
      });
    } catch (error) {
      const errorMessage = (error as Error).message || 'Unknown error';
      setResendOTPError(errorMessage);
      logErrorEvent(errorMessage, SourceName.EMAIL_VERIFICATION);
    }
  };

  if (isAutoSubmittingFromDeeplink) {
    return (
      <View style={styles.topContainer}>
        <View style={styles.container}>
          <View style={styles.subContainer}>
            <Stack.Screen
              options={{
                title: '',
                headerShown: true,
                headerBackVisible: true,
                header: (props: NativeStackHeaderProps) => (
                  <ExpoNavigationBar {...props} primaryOnPress={logout} />
                ),
              }}
            />
            <View style={styles.overlay}>
              <LoadingSpinner />
            </View>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.topContainer}>
      <View style={styles.container}>
        <View style={styles.subContainer}>
          <Stack.Screen
            options={{
              title: '',
              headerShown: true,
              headerBackVisible: true,
              header: (props: NativeStackHeaderProps) => (
                <ExpoNavigationBar {...props} primaryOnPress={logout} />
              ),
            }}
          />

          <TitleLgText style={styles.headingContainer} numberOfLines={0}>
            {translatedText('header')}
          </TitleLgText>
          {isWeb ? (
            <BodyBaseText style={styles.subHeadingContainer} numberOfLines={0}>
              {translatedText('subHeader')}
              <AmplitudeMask>
                <BodyBaseBoldText numberOfLines={0}>
                  {emailAddress}
                </BodyBaseBoldText>
              </AmplitudeMask>
            </BodyBaseText>
          ) : (
            <AmplitudeMask style={styles.subHeadingContainer}>
              <BodyBaseText numberOfLines={0}>
                {translatedText('subHeader')}
                <BodyBaseBoldText numberOfLines={0}>
                  {emailAddress}
                </BodyBaseBoldText>
              </BodyBaseText>
            </AmplitudeMask>
          )}

          <BodySmText numberOfLines={0}>
            {translatedText('description')}
            <HyperlinkSmText
              numberOfLines={0}
              testID="Enter Code"
              onPress={onEnterCodeSubmitAction}
              analyticsId="enterCodeClicked"
              customAttributes={{
                verification_type: 'email',
              }}
            >
              {translatedText('hyperlink')}
            </HyperlinkSmText>
          </BodySmText>
        </View>
        {isVerifyChallengeLoading || isWhereToGoFetching ? (
          <View style={styles.overlay}>
            <LoadingSpinner />
          </View>
        ) : null}
      </View>
      <View style={[styles.buttonContainer, { paddingBottom: insets.bottom }]}>
        <Button
          label={translatedText('buttons.resend')}
          onPress={onResendLinkAction}
          size="LARGE"
          type="PRIMARY"
          width="FULL"
          isLoading={isResendLoading}
        />
      </View>
      {snackBarMessage ? (
        <View style={styles.snackbarContainer}>
          <Snackbar text={snackBarMessage} duration={SNACKBAR_TIMEOUT} />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    display: 'flex',
    flex: 1,
    flexGrow: 1,
    justifyContent: 'flex-end',
    marginBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    flexGrow: 1,
    justifyContent: 'flex-start',
    padding: theme.spacing.m,
  },
  headingContainer: {
    color: theme.color.text.primary,
    marginBottom: theme.spacing.m,
  },
  overlay: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    justifyContent: 'center',
  },
  snackbarContainer: {
    bottom: Platform.OS === 'web' ? 18 : 0,
  },
  subContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingVertical: theme.spacing.m,
    width: '100%',
  },
  subHeadingContainer: {
    color: theme.color.text.primary,
    marginBottom: theme.spacing.m,
  },
  topContainer: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    flexGrow: 1,
  },
});

export default VerifyEmail;
